import { VCardPreviewType } from '@/lib/types/VCardT'
import { getVCardSocial } from '@/constants/VCard'
import { buildAddressString } from '@/lib/helpers/buildAddressString'
import { getFileIcon } from '@/lib/helpers/fileHelpers'
import { formatPhoneNumber, getWordInitials } from '@/lib/utils'
import { AA_LARGE, AA_TEXT, ensureContrast } from '@/lib/color/contrast'
import { IconTypes } from '@/components/Icon'

/**
 * Shared vocabulary for the six email-signature templates.
 *
 * Both halves of every template — the on-screen preview and the email-safe table export —
 * read from the same normalised model so the two can't drift apart. Colours here are fixed
 * hex values rather than theme tokens on purpose: a signature is pasted onto a white message
 * body, so it must look the same regardless of the app's light/dark theme.
 */

export type SignatureProps = Partial<VCardPreviewType> & {
  contactDownloadUrl?: string
  /**
   * Absolute origin used to resolve `/social/*.svg` style asset paths in the exported markup.
   * Empty on the server; filled in after mount so the copied HTML keeps working once it is
   * pasted into a mail client that has no idea where the app lives.
   */
  assetBaseUrl?: string
}

/**
 * Paper palette. These are the LiveContact tokens resolved to fixed hex on purpose: a
 * signature is pasted onto a white message body, so it must look identical regardless of the
 * app's light/dark theme, and no mail client resolves CSS variables.
 */
export const PAPER = {
  ink: '#0F1112', // --lc-ink
  inkMuted: '#424A4F', // --lc-ink-3
  inkSubtle: '#5A6D90', // --lc-ink-4
  inkFaint: '#8A93A6', // muted-4 lightened, for parentheticals and glyphs
  divider: '#C4CAD6', // --lc-line-2, darkened so a separator dot is actually visible
  hairline: '#E9E9E9', // --lc-line
  hairlineSoft: '#F0F2F4',
  chip: '#F3F4F6', // --lc-surface-2
  tileBorder: '#E9E9E9',
  white: '#FFFFFF',
} as const

/**
 * One type scale for every template. Sizes are px and line heights are px too — mail clients
 * disagree on what a unitless line height means, and Outlook needs an exact value to stop
 * Word from re-flowing the block.
 */
export const TYPE = {
  name: { size: 16, line: 22, weight: 600, tracking: '-0.2px' },
  nameLarge: { size: 20, line: 26, weight: 600, tracking: '-0.4px' },
  role: { size: 13, line: 19 },
  row: { size: 13, line: 20 },
  small: { size: 12, line: 18 },
} as const

/** Web-safe stacks only — no webfont survives a paste into a mail client. */
export const EMAIL_FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
export const EMAIL_SERIF_FONT = 'Georgia, "Times New Roman", Times, serif'

/**
 * Line glyphs leading the contact rows, matching the Figma design. They ship as hosted SVGs
 * (the same mechanism the social marks already use) and are resolved to absolute URLs in the
 * export, so a pasted signature still finds them. Every glyph carries alt text, so a client
 * that refuses to load images shows the label instead of a gap.
 */
export const GLYPH = {
  phone: '/icons/signature/phone.svg',
  email: '/icons/signature/mail.svg',
  website: '/icons/signature/globe.svg',
  address: '/icons/signature/pin.svg',
  link: '/icons/signature/link.svg',
  file: '/icons/signature/file.svg',
  download: '/icons/signature/download.svg',
} as const

export const GLYPH_ALT: Record<keyof typeof GLYPH, string> = {
  phone: 'Phone',
  email: 'Email',
  website: 'Website',
  address: 'Address',
  link: 'Link',
  file: 'File',
  download: 'Download',
}

/** Gutter between the glyph column and the value it labels. */
export const GLYPH_GUTTER = 8

/**
 * Geometry for a glyph-led contact row. The glyphs are drawn on a 24-unit grid centred on
 * (12, 12), so squaring the image and centring that square on the *first* line box is all it
 * takes to line every row up: the icon's optical centre then sits on the text's midline, and
 * a value that wraps (an address) still hangs below its glyph instead of dragging it down.
 */
export const glyphMetrics = (box: number, lineHeight: number) => ({
  box,
  columnWidth: box + GLYPH_GUTTER,
  // Half-pixel steps only: mail clients round sub-pixel padding unpredictably.
  offset: Math.max(0, Math.round(((lineHeight - box) / 2) * 2) / 2),
})

/**
 * Mixes a hex colour towards white. Used for the initials fallback: a saturated brand square
 * reads as an app avatar, a brand-tinted one with brand initials reads as a wordmark.
 */
export const tint = (hex: string, amount: number) => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return '#EEF3FE'

  const channel = (offset: number) => {
    const value = parseInt(match[1].slice(offset, offset + 2), 16)
    return Math.round(value + (255 - value) * amount)
      .toString(16)
      .padStart(2, '0')
  }

  return `#${channel(0)}${channel(2)}${channel(4)}`.toUpperCase()
}

export const stripProtocol = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/+$/, '')

export const absoluteAsset = (path: string, base?: string) =>
  base && path.startsWith('/') ? `${base}${path}` : path

export type SignatureSocial = {
  type: string
  url: string
  name: string
  svgImg: string
}

export type SignatureFile = {
  id: number
  name: string
  url: string
  icon: IconTypes
}

export type SignatureModel = {
  brand: string
  /**
   * The brand as **text** on the signature's white paper, at 4.5:1. A pale brand darkens just
   * enough to be read; everything else is passed through untouched.
   */
  brandInk: string
  /**
   * The brand as a **rule, chip or fill carrying white artwork**, at 3:1 on white. Keeps a
   * near-white brand from turning a 2px accent rule and the social chips into blank space.
   */
  brandEdge: string
  /** Display name: the person when we have one, otherwise the card's representative name. */
  name: string
  initials: string
  /** Logo/avatar source, empty when the card only has a generated placeholder. */
  logoSrc: string
  /** `Job position · Company · Industry`, de-duplicated and emptied of blanks. */
  meta: string[]
  jobPosition: string
  company: string
  phones: { key: string; value: string; label: string; href: string }[]
  emails: { key: string; value: string; label: string; href: string }[]
  website: string
  websiteLabel: string
  addresses: { key: string; value: string; label: string }[]
  socials: SignatureSocial[]
  files: SignatureFile[]
  links: { key: string; label: string; url: string }[]
  notes: string
  downloadUrl: string
  assetBaseUrl: string
}

/**
 * Normalises the raw vCard shape into the fields the templates actually render. Nothing is
 * dropped: every value the card carries lands somewhere in the model, and each template
 * decides how much of it its layout can show.
 */
export const buildSignatureModel = ({
  mainColor,
  avatar,
  representativeName,
  firstName,
  lastName,
  industry,
  jobPosition,
  unverifiedPhones,
  emails,
  website,
  addresses,
  socialLinks,
  notes,
  defaultLinks,
  isAvatarGenerated,
  companyFileItems,
  contactDownloadUrl,
  assetBaseUrl,
}: SignatureProps): SignatureModel => {
  const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim()
  const name = fullName || representativeName || ''
  // `representativeName` doubles as the organisation on member cards; when it is the only
  // name we have it is already used as the display name, so `industry` carries the company.
  const company = (fullName && representativeName ? representativeName : industry) || ''
  const position = jobPosition || ''

  const meta = Array.from(
    new Set([position, company, fullName && representativeName ? industry || '' : ''])
  ).filter((part): part is string => Boolean(part && part !== name))

  const brand = mainColor || '#346EF3'

  return {
    brand,
    brandInk: ensureContrast(brand, PAPER.white, AA_TEXT),
    brandEdge: ensureContrast(brand, PAPER.white, AA_LARGE),
    name,
    initials: getWordInitials(name),
    logoSrc: typeof avatar === 'string' && avatar && !isAvatarGenerated ? avatar : '',
    meta,
    jobPosition: position,
    company,
    phones: (unverifiedPhones ?? []).map(({ phone, label }, index) => ({
      key: `${phone}-${index}`,
      value: formatPhoneNumber(phone),
      label: label || '',
      href: `tel:${phone}`,
    })),
    emails: (emails ?? []).map(({ value, alias }, index) => ({
      key: `${value}-${index}`,
      value,
      label: alias || '',
      href: `mailto:${value}`,
    })),
    website: website || '',
    websiteLabel: website ? stripProtocol(website) : '',
    addresses: (addresses ?? [])
      .map((address, index) => ({
        key: `address-${index}`,
        value: buildAddressString(address),
        label: address.label || '',
      }))
      .filter(({ value }) => Boolean(value)),
    socials: (socialLinks ?? []).reduce<SignatureSocial[]>((acc, { type, url }) => {
      const field = getVCardSocial(type)
      if (field) {
        acc.push({ type, url, name: field.name, svgImg: field.svgImg })
      }
      return acc
    }, []),
    files: (companyFileItems ?? []).map(({ id, name: fileName, file }) => ({
      id,
      name: fileName,
      url: file.permanentUrl,
      icon: getFileIcon(file.mimeType, file.type) as IconTypes,
    })),
    links: (defaultLinks ?? []).map(({ label, url }, index) => ({
      key: `${url}-${index}`,
      label: label || stripProtocol(url),
      url,
    })),
    notes: notes || '',
    downloadUrl: contactDownloadUrl || '',
    assetBaseUrl: assetBaseUrl || '',
  }
}

/**
 * The `text/plain` flavour of the copied signature.
 *
 * Every clipboard write carries both flavours: mail clients that accept rich paste take the
 * HTML, and plain-text composers (and the "paste without formatting" many people reach for)
 * get something readable instead of the HTML source or a single run-together line.
 */
export const signatureToPlainText = (model: SignatureModel): string => {
  const lines: string[] = []

  if (model.name) lines.push(model.name)
  if (model.meta.length) lines.push(model.meta.join(' · '))

  const details: string[] = [
    ...model.phones.map(({ value, label }) => (label ? `${value} (${label})` : value)),
    ...model.emails.map(({ value, label }) => (label ? `${value} (${label})` : value)),
    ...(model.websiteLabel ? [model.websiteLabel] : []),
    ...model.addresses.map(({ value, label }) => (label ? `${label}: ${value}` : value)),
    ...model.links.map(({ label, url }) => `${label} — ${url}`),
    ...model.files.map(({ name, url }) => `${name} — ${url}`),
  ]
  if (details.length) lines.push('', ...details)

  const trailing = [
    ...model.socials.map(({ name, url }) => `${name}: ${url}`),
    ...(model.downloadUrl ? [`Save contact: ${model.downloadUrl}`] : []),
  ]
  if (trailing.length) lines.push('', ...trailing)

  if (model.notes) lines.push('', model.notes)

  return lines.join('\n')
}
